/**
 * Font weight and style constants and utilities
 * Centralized definitions for font name parsing, weight detection, and style detection
 */

// Standard weight names (from thickest to thinnest)
export const WEIGHT_ORDER: string[] = [
  "Black",
  "Heavy",
  "Ultra-Bold",
  "ExtraBold",
  "Bold",
  "Semi-Bold",
  "Medium",
  "Normal",
  "Regular", // Regular is equivalent to Normal but kept separate for display
  "Light",
  "Extra-Light",
  "Thin",
];

export type WeightName = string;

// Recognized weight names and abbreviations
export const RECOGNIZED_WEIGHTS: readonly string[] = [
  "bold",
  "semibold",
  "semi-bold",
  "medium",
  "regular",
  "reg",
  "normal",
  "light",
  "thin",
  "extralight",
  "extra-light",
  "extrabold",
  "extra-bold",
  "black",
  "heavy",
  "ultrabold",
  "ultra-bold",
  // Abbreviations
  "re",
  "bd",
  "bld",
  "md",
  "lt",
  "sb",
  "th",
  "el",
  "eb",
  "bk",
  "hv",
  "ub",
] as const;

// Combined weight+italic patterns (e.g., "bolditalic", "heavyitalic")
export const COMBINED_WEIGHT_ITALIC_PATTERNS: readonly string[] = [
  "bolditalic",
  "boldit",
  "semibolditalic",
  "semiboldit",
  "semi-bolditalic",
  "semi-boldit",
  "mediumitalic",
  "mediumit",
  "regularitalic",
  "regularit",
  "normalitalic",
  "normalit",
  "lightitalic",
  "lightit",
  "thinitalic",
  "thinit",
  "extralightitalic",
  "extralightit",
  "extra-lightitalic",
  "extra-lightit",
  "extrabolditalic",
  "extraboldit",
  "extra-bolditalic",
  "extra-boldit",
  "blackitalic",
  "blackit",
  "heavyitalic",
  "heavyit",
  "ultrabolditalic",
  "ultraboldit",
  "ultra-bolditalic",
  "ultra-boldit",
] as const;

// Regex patterns for extracting weight from font names
export const WEIGHT_PATTERNS = [
  // Full names with hyphen (extrabold/extra-bold BEFORE extralight/extra-light to avoid partial matches)
  /-(bold|semibold|semi-bold|medium|regular|reg|normal|light|thin|extrabold|extra-bold|extralight|extra-light|black|heavy|ultrabold|ultra-bold)\s*\d*$/i,
  // Abbreviations with hyphen: -re, -bd, -md, -lt, -sb, -eb, etc. (eb/extrabold BEFORE el/extralight)
  /-(re|reg|bd|bld|md|lt|sb|th|eb|extrabold|el|extralight|bk|black|hv|heavy|ub|ultrabold)\s*\d*$/i,
  // Full names without hyphen (extrabold/extra-bold BEFORE extralight/extra-light)
  /(bold|semibold|semi-bold|medium|regular|reg|normal|light|thin|extrabold|extra-bold|extralight|extra-light|black|heavy|ultrabold|ultra-bold)\s*\d*$/i,
  // Abbreviations without hyphen: re, bd, md, lt, sb, eb, etc. (eb/extrabold BEFORE el/extralight)
  /(re|reg|bd|bld|md|lt|sb|th|eb|extrabold|el|extralight|bk|black|hv|heavy|ub|ultrabold)\s*\d*$/i,
  // Full names at the end (extrabold/extra-bold BEFORE extralight/extra-light)
  /(bold|semibold|semi-bold|medium|regular|reg|normal|light|thin|extrabold|extra-bold|extralight|extra-light|black|heavy|ultrabold|ultra-bold)$/i,
];

// Regex patterns for extracting weight from italic font names
export const ITALIC_WEIGHT_PATTERNS = [
  // Patterns like "BlackItalic", "BoldItalic", "ExtraBoldItalic", etc.
  // IMPORTANT: extrabold/extra-bold BEFORE bold to avoid partial matches
  /(black|heavy|ultrabold|ultra-bold|extrabold|extra-bold|bold|semibold|semi-bold|medium|regular|normal|light|extralight|extra-light|thin)italic/i,
  /(black|heavy|ultrabold|ultra-bold|extrabold|extra-bold|bold|semibold|semi-bold|medium|regular|normal|light|extralight|extra-light|thin)it\b/i,
  // Patterns with hyphen like "-BlackItalic", "-BoldItalic", "-ExtraBoldItalic"
  // IMPORTANT: extrabold/extra-bold BEFORE bold to avoid partial matches
  /-(black|heavy|ultrabold|ultra-bold|extrabold|extra-bold|bold|semibold|semi-bold|medium|regular|normal|light|extralight|extra-light|thin)italic/i,
  /-(black|heavy|ultrabold|ultra-bold|extrabold|extra-bold|bold|semibold|semi-bold|medium|regular|normal|light|extralight|extra-light|thin)it\b/i,
  // Abbreviations
  // IMPORTANT: eb/extrabold BEFORE bd/bold to avoid partial matches
  /(bk|hv|ub|ultrabold|eb|extrabold|bd|bld|sb|md|re|reg|normal|lt|el|th)italic/i,
  /(bk|hv|ub|ultrabold|eb|extrabold|bd|bld|sb|md|re|reg|normal|lt|el|th)it\b/i,
  /-(bk|hv|ub|ultrabold|eb|extrabold|bd|bld|sb|md|re|reg|normal|lt|el|th)italic/i,
  /-(bk|hv|ub|ultrabold|eb|extrabold|bd|bld|sb|md|re|reg|normal|lt|el|th)it\b/i,
];

// Regex patterns for extracting weight from condensed font names
export const CONDENSED_WEIGHT_PATTERNS = [
  /(?:^|[-_\s])(black)(?:extra|ultra)?(?:condensed|cond|cn|semicn|semicond)/i,
  /(?:^|[-_\s])(heavy)(?:extra|ultra)?(?:condensed|cond|cn|semicn|semicond)/i,
  /(?:^|[-_\s])(ultra[-_]?bold|ultrabold)(?:extra|ultra)?(?:condensed|cond|cn|semicn|semicond)/i,
  /(?:^|[-_\s])(extra[-_]?bold|extrabold)(?:extra|ultra)?(?:condensed|cond|cn|semicn|semicond)/i,
  /(?:^|[-_\s])(bold)(?:extra|ultra)?(?:condensed|cond|cn|semicn|semicond)/i,
  /(?:^|[-_\s])(semi[-_]?bold|semibold)(?:condensed|cond|cn|semicn|semicond)/i,
  /(?:^|[-_\s])(medium)(?:condensed|cond|cn|semicn|semicond)/i,
  /(?:^|[-_\s])(regular|normal)(?:condensed|cond|cn|semicn|semicond)/i,
  /(?:^|[-_\s])(light)(?:condensed|cond|cn|semicn|semicond)/i,
  /(?:^|[-_\s])(thin)(?:condensed|cond|cn|semicn|semicond)/i,
];

// Helper function to check if a string contains italic patterns
export function isItalicFontName(fontName: string): boolean {
  const fontNameLower = fontName.toLowerCase();
  return (
    fontNameLower.includes("italic") ||
    fontNameLower.includes("it") ||
    fontNameLower.includes("bolditalic") ||
    fontNameLower.includes("regularitalic") ||
    fontNameLower.includes("normalitalic") ||
    fontNameLower.includes("mediumitalic") ||
    fontNameLower.includes("lightitalic") ||
    fontNameLower.includes("thinitalic") ||
    fontNameLower.includes("extralightitalic") ||
    fontNameLower.includes("extra-lightitalic") ||
    fontNameLower.includes("semibolditalic") ||
    fontNameLower.includes("semi-bolditalic") ||
    fontNameLower.includes("ultrabolditalic") ||
    fontNameLower.includes("ultra-bolditalic") ||
    fontNameLower.includes("blackitalic") ||
    fontNameLower.includes("heavyitalic") ||
    fontNameLower.includes("bditalic") ||
    fontNameLower.includes("blditalic") ||
    fontNameLower.includes("reitalic") ||
    fontNameLower.includes("regitalic") ||
    fontNameLower.includes("mditalic") ||
    fontNameLower.includes("ltitalic") ||
    fontNameLower.includes("thitalic") ||
    fontNameLower.includes("elitalic") ||
    fontNameLower.includes("sbitalic") ||
    fontNameLower.includes("ubitalic") ||
    fontNameLower.includes("bkitalic") ||
    fontNameLower.includes("hvitalic")
  );
}

// Helper function to check if a string contains condensed patterns
export function isCondensedFontName(fontName: string): boolean {
  const fontNameLower = fontName.toLowerCase();
  return (
    fontNameLower.includes("condensed") ||
    fontNameLower.includes("cond") ||
    fontNameLower.includes("cn") ||
    fontNameLower.includes("semicn") ||
    fontNameLower.includes("semicond") ||
    fontNameLower.includes("extracondensed") ||
    fontNameLower.includes("extra-condensed") ||
    fontNameLower.includes("ultracondensed") ||
    fontNameLower.includes("ultra-condensed") ||
    fontNameLower.includes("boldcondensed") ||
    fontNameLower.includes("boldcond") ||
    fontNameLower.includes("boldcn") ||
    fontNameLower.includes("blackcondensed") ||
    fontNameLower.includes("blackcond") ||
    fontNameLower.includes("blackcn") ||
    fontNameLower.includes("regularcondensed") ||
    fontNameLower.includes("regularcond") ||
    fontNameLower.includes("regularcn") ||
    fontNameLower.includes("mediumcondensed") ||
    fontNameLower.includes("mediumcond") ||
    fontNameLower.includes("mediumcn") ||
    fontNameLower.includes("lightcondensed") ||
    fontNameLower.includes("lightcond") ||
    fontNameLower.includes("lightcn") ||
    fontNameLower.includes("thincondensed") ||
    fontNameLower.includes("thincond") ||
    fontNameLower.includes("thincn") ||
    fontNameLower.includes("semiboldcondensed") ||
    fontNameLower.includes("semiboldcond") ||
    fontNameLower.includes("semiboldcn") ||
    fontNameLower.includes("extraboldcondensed") ||
    fontNameLower.includes("extraboldcond") ||
    fontNameLower.includes("extraboldcn")
  );
}

// Helper function to check if a string contains condensed patterns (simplified version for basic checks)
export function isCondensedFontNameBasic(fontName: string): boolean {
  const fontNameLower = fontName.toLowerCase();
  return (
    fontNameLower.includes("condensed") ||
    fontNameLower.includes("cond") ||
    fontNameLower.includes("cn") ||
    fontNameLower.includes("semicn") ||
    fontNameLower.includes("semicond")
  );
}

/**
 * Normalize weight name to standard format
 * Converts various weight names and abbreviations to standardized names
 */
export function normalizeWeightName(weight: string): string {
  const normalized = weight.toLowerCase().replace(/-/g, "").trim();

  // Handle abbreviations first
  if (normalized === "re" || normalized === "reg") return "Regular";
  if (normalized === "bd" || normalized === "bld") return "Bold";
  if (normalized === "md") return "Medium";
  if (normalized === "lt") return "Light";
  if (normalized === "sb") return "Semi-Bold";
  if (normalized === "th" || normalized === "thin") return "Thin";
  if (normalized === "el" || normalized === "extralight" || normalized === "extra-light") return "Extra-Light";
  if (normalized === "eb" || normalized === "extrabold" || normalized === "extra-bold") return "ExtraBold";
  if (normalized === "bk" || normalized === "black") return "Black";
  if (normalized === "hv" || normalized === "heavy") return "Heavy";
  if (normalized === "ub" || normalized === "ultrabold" || normalized === "ultra-bold") return "Ultra-Bold";

  // Handle full names
  if (normalized === "normal") return "Normal";
  if (normalized === "regular") return "Regular"; // Keep Regular as Regular, don't convert to Normal
  if (normalized === "semibold" || normalized === "semi-bold") return "Semi-Bold";
  if (normalized === "bold") return "Bold";
  if (normalized === "extrabold" || normalized === "extra-bold") return "ExtraBold";
  if (normalized === "medium") return "Medium";
  if (normalized === "light") return "Light";
  if (normalized === "thin") return "Thin";
  if (normalized === "extralight" || normalized === "extra-light") return "Extra-Light";
  if (normalized === "black") return "Black";
  if (normalized === "heavy") return "Heavy";
  if (normalized === "ultrabold" || normalized === "ultra-bold") return "Ultra-Bold";

  // Return capitalized version if not recognized
  return weight.charAt(0).toUpperCase() + weight.slice(1).toLowerCase();
}

/**
 * Convert weight name to numeric CSS weight value
 * Returns null if the weight cannot be converted
 */
export function getFontWeightValue(weightName: string): number | null {
  const normalized = weightName.toLowerCase().replace(/-/g, "").trim();

  // Map to standard numeric weights
  if (normalized === "normal" || normalized === "regular") return 400;
  if (normalized === "bold") return 700;
  if (normalized === "semibold" || normalized === "semi-bold") return 600;
  if (normalized === "extrabold" || normalized === "extra-bold") return 800;
  if (normalized === "light") return 300;
  if (normalized === "medium") return 500;
  if (normalized === "thin") return 100;
  if (normalized === "extralight" || normalized === "extra-light") return 200;
  if (normalized === "black") return 900;
  if (normalized === "heavy") return 900;
  if (normalized === "ultrabold" || normalized === "ultra-bold") return 850;

  return null;
}

/**
 * Convert weight name to FontFace-compatible weight value
 * Returns "normal" or "bold" for string values, or numeric values
 */
export function getFontFaceWeight(weightName: string): string | number {
  const normalized = weightName.toLowerCase().trim();

  if (normalized === "thin") return 100;
  if (normalized === "extra-light" || normalized === "extralight") return 200;
  if (normalized === "light") return 300;
  if (normalized === "normal" || normalized === "regular") return "normal";
  if (normalized === "medium") return 500;
  if (normalized === "semi-bold" || normalized === "semibold") return 600;
  if (normalized === "bold") return "bold";
  if (normalized === "extrabold" || normalized === "extra-bold") return 800;
  if (normalized === "ultra-bold" || normalized === "ultrabold") return 850;
  if (normalized === "black" || normalized === "heavy") return 900;

  return "normal"; // Default fallback
}

/**
 * Map numeric weight value to weight name
 * Used for converting OS/2 usWeightClass or FontFace.weight (number) to weight name
 */
export function mapNumericWeightToName(weight: number): string {
  if (weight <= 150) return "Thin";
  if (weight <= 250) return "Extra-Light";
  if (weight <= 350) return "Light";
  if (weight <= 450) return "Normal";
  if (weight <= 550) return "Medium";
  if (weight <= 650) return "Semi-Bold";
  if (weight <= 750) return "Bold";
  if (weight <= 800) return "ExtraBold";
  if (weight <= 850) return "Ultra-Bold";
  return "Black";
}

/**
 * Map string weight value to weight name
 * Used for converting FontFace.weight (string) to weight name
 */
export function mapStringWeightToName(weight: string): string {
  const normalized = weight.toLowerCase().trim();
  if (normalized === "normal" || normalized === "400") return "Normal";
  if (normalized === "bold" || normalized === "700") return "Bold";
  if (normalized === "300") return "Light";
  if (normalized === "500") return "Medium";
  if (normalized === "600") return "Semi-Bold";
  return normalizeWeightName(normalized);
}
